/**
 * Geometria megamapy podwykonawców — bez biblioteki GIS.
 *
 * Granice powiatów z backendu (GET /county-boundaries/) mają IDENTYCZNE wierzchołki na
 * wspólnej granicy sąsiadów (budowane z jednej uproszczonej sieci linii, zob.
 * backend/apps/geo/boundaries.py). Dzięki temu:
 * - obszar firmy to po prostu jej powiaty złożone w jeden MultiPolygon — jedna ścieżka
 *   SVG, więc na styku powiatów nie ma prześwitów antyaliasingu;
 * - obrys obszaru (i granice województw) to krawędzie, które w zbiorze powiatów
 *   występują nieparzyście (wspólna krawędź dwóch powiatów zbioru — dwa razy).
 */

/** [lng, lat] — kolejność GeoJSON. */
export type Position = [number, number];
/** Pierścienie wielokąta: zewnętrzny + dziury. */
export type PolygonCoords = Position[][];

export type CountyGeometry =
  | { type: "Polygon"; coordinates: PolygonCoords }
  | { type: "MultiPolygon"; coordinates: PolygonCoords[] };

export interface CountyFeature {
  type: "Feature";
  properties: { teryt: string; name: string; voivodeship: string };
  geometry: CountyGeometry;
}

export function polygonsOf(geometry: CountyGeometry): PolygonCoords[] {
  return geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
}

/** Wielokąty wskazanych powiatów jako jeden MultiPolygon; nieznane kody pomija. */
export function mergedPolygons(
  byCode: Map<string, CountyFeature>,
  codes: Iterable<string>,
): PolygonCoords[] {
  const out: PolygonCoords[] = [];
  for (const code of codes) {
    const feature = byCode.get(code);
    if (feature) out.push(...polygonsOf(feature.geometry));
  }
  return out;
}

interface Edge {
  key: string;
  a: Position;
  b: Position;
  count: number;
}

const pointKey = (p: Position) => `${p[0]},${p[1]}`;

function edgeCounts(polygons: PolygonCoords[]): Map<string, Edge> {
  const edges = new Map<string, Edge>();
  for (const polygon of polygons) {
    for (const ring of polygon) {
      for (let i = 0; i + 1 < ring.length; i++) {
        const a = ring[i];
        const b = ring[i + 1];
        const ka = pointKey(a);
        const kb = pointKey(b);
        if (ka === kb) continue; // zero-length after coordinate rounding
        const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
        const edge = edges.get(key);
        if (edge) edge.count += 1;
        else edges.set(key, { key, a, b, count: 1 });
      }
    }
  }
  return edges;
}

/** Skleja luźne odcinki w możliwie długie linie (mniej obiektów do narysowania). */
function chain(edges: Edge[]): Position[][] {
  const byPoint = new Map<string, number[]>();
  edges.forEach((e, i) => {
    for (const p of [e.a, e.b]) {
      const k = pointKey(p);
      const list = byPoint.get(k);
      if (list) list.push(i);
      else byPoint.set(k, [i]);
    }
  });
  const used = new Array<boolean>(edges.length).fill(false);
  const nextFrom = (p: Position): Position | null => {
    for (const i of byPoint.get(pointKey(p)) ?? []) {
      if (used[i]) continue;
      used[i] = true;
      const e = edges[i];
      return pointKey(e.a) === pointKey(p) ? e.b : e.a;
    }
    return null;
  };
  const lines: Position[][] = [];
  edges.forEach((e, i) => {
    if (used[i]) return;
    used[i] = true;
    const line: Position[] = [e.a, e.b];
    for (let p = nextFrom(e.b); p; p = nextFrom(line[line.length - 1])) line.push(p);
    const head: Position[] = [];
    for (let p = nextFrom(e.a); p; p = nextFrom(head[head.length - 1])) head.push(p);
    lines.push([...head.reverse(), ...line]);
  });
  return lines;
}

/** Zewnętrzny obrys sumy wielokątów (granice wewnętrzne między nimi znikają). */
export function outlineOf(polygons: PolygonCoords[]): Position[][] {
  return chain([...edgeCounts(polygons).values()].filter((e) => e.count % 2 === 1));
}

/** Granice między grupami (np. województwami) i granica zewnętrzna — każda raz. */
export function groupBorders(groups: PolygonCoords[][]): Position[][] {
  const borders = new Map<string, Edge>();
  for (const polygons of groups) {
    for (const edge of edgeCounts(polygons).values()) {
      if (edge.count % 2 === 1) borders.set(edge.key, edge);
    }
  }
  return chain([...borders.values()]);
}

/** Granice województw (i granica kraju) z granic powiatów — powiaty grupowane po `voivodeship`. */
export function voivodeshipBorders(counties: CountyFeature[]): Position[][] {
  const groups = new Map<string, PolygonCoords[]>();
  for (const f of counties) {
    const list = groups.get(f.properties.voivodeship) ?? [];
    list.push(...polygonsOf(f.geometry));
    groups.set(f.properties.voivodeship, list);
  }
  return groupBorders([...groups.values()]);
}

/** [[south, west], [north, east]] wielokątów albo null dla pustego zbioru. */
export function boundsOf(polygons: PolygonCoords[]): [[number, number], [number, number]] | null {
  let south = Infinity;
  let west = Infinity;
  let north = -Infinity;
  let east = -Infinity;
  for (const polygon of polygons) {
    for (const [lng, lat] of polygon[0] ?? []) {
      if (lat < south) south = lat;
      if (lat > north) north = lat;
      if (lng < west) west = lng;
      if (lng > east) east = lng;
    }
  }
  return Number.isFinite(south) ? [[south, west], [north, east]] : null;
}

// Mocne, dobrze rozróżnialne kolory na jasnym podkładzie (obrys + półprzezroczyste
// wypełnienie); dalsze firmy dostają odcienie co „złoty kąt”.
const PALETTE = [
  "#e6194b", "#3cb44b", "#4363d8", "#f58231", "#911eb4", "#0fa3b1",
  "#f032e6", "#7cb518", "#469990", "#9a6324", "#800000", "#808000",
  "#000075", "#d4a017", "#c2185b", "#5d4037",
];

/** Kolor firmy po jej pozycji na PEŁNEJ liście — nie zmienia się przy filtrowaniu. */
export function contractorColor(index: number): string {
  if (index < PALETTE.length) return PALETTE[index];
  const hue = Math.round(((index - PALETTE.length) * 137.508 + 20) % 360);
  return `hsl(${hue}, 70%, 40%)`;
}
